import { _ } from "../../../../core/polyglot/polyglot.js";
import { nonEmptyValueOrDefault } from "../../../../core/helpers/object-util.js";
import jQuery from "jquery";

/**
 * Convert a ProseMirror updateNodeAttributes view content by async fetching a value
 * and injecting it in the editor. Then the `onLoad` callback is triggered
 * so that the InputValueAction can alter the editor state to take the
 * fetched value into account.
 *
 * @param {HTMLElement} container - DOM node inserted in the editor
 * @param {function(object): void} updateNodeAttributes - Function to alter the attributes of the
 *   editor node
 * @param {string} type - Type of input value converted
 * @param {function} fetcher - Parameterless async function used to fetch the value
 *   corresponding to the input value. This function resolves to an object with a
 *   {string} value and an optional {string} title
 * @param {function} onLoad - Callback triggered when the value has been fetched
 *   and inserted. Invoked with the fetched value as argument
 * @param {function} [makePopover] - Builder used to make a popover that will
 *   open on click. When this argument is falsy, no popover is built
 * @param {function(object):string} getValue - Function taking the fetched object as parameter
 *   and returning the value to inject in the editor
 * @param {function(object):string} [getTitle] - Function taking the fetched object as parameter
 *   and returning the title of the editor node. If not provided, no title will be shown
 * @param {ProseMirror.Node} node - Node to convert
 */
export default function inputValueConvertor({
	container,
	updateNodeAttributes,
	type,
	fetcher,
	onLoad,
	makePopover,
	getValue,
	getTitle,
	node,
}) {
	container.classList.add("input-value");
	container.classList.add(`input-value-type_${type}`);

	setAsLoading({ container, node });

	fetchData({
		fetcher,
		updateNodeAttributes,
		container,
		onLoad,
		makePopover,
		getValue,
		getTitle,
	});
}

function setupPopover({
	content,
	updateNodeAttributes,
	container,
	makePopover,
}) {
	if (!makePopover) {
		return;
	}

	const getTriggerItem = () => {
		const triggerItem = container.childNodes[0];
		triggerItem.asJQuery = () => jQuery(triggerItem);
		return triggerItem;
	};

	let popover = null;

	popover = makePopover({
		updateNodeAttributes: (...specs) => {
			updateNodeAttributes(...specs);
			popover?.setTriggerItem(getTriggerItem());
		},
		getTriggerItem,
		content,
	});
}

function setAsLoading({ container, node }) {
	if (node.content.size) {
		container.innerHTML = `<div class="input-value-loading">${getNodeContent(
			node,
		)}</div>`;
	} else {
		container.innerHTML = '<div class="input-value-loader"></div>';
	}
}

function getNodeContent(node) {
	return node.content.content[0].text;
}

async function fetchData({
	fetcher,
	updateNodeAttributes,
	container,
	onLoad,
	makePopover,
	getValue,
	getTitle,
}) {
	let fetchedData;

	try {
		fetchedData = await fetcher();
	} catch (error) {
		// eslint-disable-next-line no-console -- this is for dev purposes
		console.error(error);

		container.innerHTML = `<span class="input-value-error">${_(
			"Error",
		).toString()}</span>`;

		container.title = _("Error while fetching the value").toString();

		return;
	}

	let value = nonEmptyValueOrDefault({ value: getValue(fetchedData) });

	if (value === "-") {
		value = _("N/A").toString();
	}

	container.innerHTML = `<span class="input-value-content">${value}</span>`;

	setupPopover({
		container,
		updateNodeAttributes,
		content: fetchedData,
		makePopover,
	});

	if (getTitle) {
		container.title = getTitle(fetchedData);
	}

	onLoad(value);
}
